// Pestaña "Pagos de Clientes (VB)" — bandeja de abonos con cascada de cierre.
import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { fmtFecha } from '../../lib/utils';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { CheckCircle2, Loader2, Pencil, Sparkles, ShieldCheck, RefreshCw, AlertTriangle, Fingerprint } from 'lucide-react';
import { toast } from 'sonner';
import { IngresoExpressDialog } from './IngresoExpressDialog';

const CLP = (v) => (v || 0).toLocaleString('es-CL');

const PROPUESTA_BADGE = {
  cobro_registrado_alegra: { cls: 'bg-sky-100 text-sky-700', label: 'Ya registrado en Alegra' },
  factura_exacta: { cls: 'bg-emerald-100 text-emerald-700', label: 'Factura calza exacto' },
  seleccionar_facturas: { cls: 'bg-amber-100 text-amber-700', label: 'Elegir facturas' },
  sin_match: { cls: 'bg-gray-100 text-gray-500', label: 'Sin propuesta' },
};

export const PagosClientesTab = ({ cargaSel, cuentaSel, onChanged }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vbLoading, setVbLoading] = useState(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [dialog, setDialog] = useState({ open: false, item: null });
  const [sel, setSel] = useState([]);
  const [cobroSel, setCobroSel] = useState(null);
  const [nota, setNota] = useState('');
  const [dupDlg, setDupDlg] = useState({ open: false, item: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = cargaSel && cargaSel !== 'todas' ? { carga_id: cargaSel } : {};
      if (cuentaSel && cuentaSel !== 'todas') params.cuenta = cuentaSel;
      const r = await api.get('/conciliacion-bancaria/pagos-clientes', { params });
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error cargando pagos de clientes');
    } finally { setLoading(false); }
  }, [cargaSel, cuentaSel]);

  useEffect(() => { load(); }, [load]);

  const darVB = async (item, payload) => {
    setVbLoading(item.movimiento.id);
    try {
      const r = await api.post(`/conciliacion-bancaria/pagos-clientes/${item.movimiento.id}/vb`, payload);
      if (r.data.alegra_errores > 0) toast.warning(r.data.message);
      else toast.success(r.data.message);
      setDialog({ open: false, item: null });
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error aplicando VB');
    } finally { setVbLoading(null); }
  };

  const vbDirecto = (item) => {
    if (item.advertencias?.length && !window.confirm(`⚠ ATENCIÓN antes de dar VB:\n\n${item.advertencias.map((a) => `• ${a}`).join('\n')}\n\n¿Confirmas que este depósito corresponde a: ${item.propuesta === 'cobro_registrado_alegra' ? item.cobro_alegra.ref_desc : `factura ${item.factura_exacta.numero}`}?`)) return;
    if (item.propuesta === 'cobro_registrado_alegra') {
      darVB(item, { usar_cobro_alegra: item.cobro_alegra.ref_id, nota: '' });
    } else if (item.propuesta === 'factura_exacta') {
      darVB(item, { alegra_invoice_ids: [String(item.factura_exacta.alegra_id)], nota: '' });
    }
  };

  const openCorregir = (item) => {
    setSel([]);
    setCobroSel(null);
    setNota('');
    setDialog({ open: true, item });
  };

  const marcarDuplicadoSinAlegra = async (item) => {
    const motivo = window.prompt('Explicación OBLIGATORIA del pago duplicado / a favor del cliente (quedará destacada de forma permanente):', '');
    if (motivo === null) return;
    if (motivo.trim().length < 5) { toast.error('Escribe la explicación'); return; }
    try {
      await api.post(`/conciliacion-bancaria/movimientos/${item.movimiento.id}/conciliar`, {
        tipo: 'pago_duplicado_cliente', ref_id: null, ref_desc: `Pago duplicado / a favor de ${item.cliente_detectado || 'cliente'}`, nota: motivo, destacar: true,
      });
      toast.warning('Marcado como pago duplicado y DESTACADO. Recuerda registrarlo en Alegra.');
      setDialog({ open: false, item: null });
      load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const toggleSel = (f) => setSel((p) => p.some((x) => x.alegra_id === f.alegra_id)
    ? p.filter((x) => x.alegra_id !== f.alegra_id) : [...p, f]);

  const sumaSel = sel.reduce((s, f) => s + (f.saldo || 0), 0);

  const vbBatch = async () => {
    setBatchLoading(true);
    try {
      const params = { dry_run: true };
      if (cargaSel && cargaSel !== 'todas') params.carga_id = cargaSel;
      if (cuentaSel && cuentaSel !== 'todas') params.cuenta = cuentaSel;
      const dry = await api.post('/conciliacion-bancaria/pagos-clientes/vb-batch', null, { params });
      const n = dry.data.aplicables;
      if (n === 0) { toast.info('No hay propuestas 100% seguras para VB masivo'); return; }
      if (!window.confirm(`Se dará VB a ${n} pago(s) con propuesta exacta:\n\n${dry.data.detalle.slice(0, 8).map((d) => `• ${fmtFecha(d.fecha)} $${CLP(d.abono)} → ${d.propuesta}`).join('\n')}${n > 8 ? `\n… y ${n - 8} más` : ''}\n\nEsto registra los pagos en Alegra y cierra las CxC. ¿Continuar?`)) return;
      const r = await api.post('/conciliacion-bancaria/pagos-clientes/vb-batch', null, { params: { ...params, dry_run: false } });
      toast.success(`VB masivo: ${r.data.aplicados} aplicados${r.data.errores_alegra ? ` (${r.data.errores_alegra} errores Alegra — revisar Flujo de Caja)` : ''}`);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error en VB masivo');
    } finally { setBatchLoading(false); }
  };

  return (
    <div className="space-y-3" data-testid="pagos-clientes-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Abonos pendientes con propuesta de cierre. El <b>VB</b> marca la CxC pagada en el Flujo de Caja,
          registra el pago en Alegra (o usa el cobro ya existente) y concilia el movimiento — todo auditado.
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="btn-refrescar-pagos">
            <RefreshCw className="mr-1 h-3.5 w-3.5" /> Refrescar
          </Button>
          <Button size="sm" onClick={vbBatch} disabled={batchLoading} data-testid="btn-vb-masivo">
            {batchLoading ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1 h-4 w-4" />}
            VB masivo (exactas)
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-slate-50 text-left text-xs text-muted-foreground">
                <th className="px-3 py-2">Fecha</th>
                <th className="px-3 py-2">Cliente detectado</th>
                <th className="px-3 py-2 text-right">Abono</th>
                <th className="px-3 py-2">Propuesta</th>
                <th className="px-3 py-2">Detalle propuesta</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="py-8 text-center text-muted-foreground"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={6} className="py-8 text-center text-muted-foreground" data-testid="pagos-empty">
                  No hay abonos pendientes de VB 🎉
                </td></tr>
              ) : items.map((it) => {
                const pb = PROPUESTA_BADGE[it.propuesta];
                const detalle = it.propuesta === 'cobro_registrado_alegra' ? it.cobro_alegra?.ref_desc
                  : it.propuesta === 'factura_exacta' ? `Factura ${it.factura_exacta.numero} ${it.factura_exacta.cliente_nombre} (saldo $${CLP(it.factura_exacta.saldo)})${it.factura_exacta.fc_cxc ? ' · CxC en flujo' : ''}`
                  : it.propuesta === 'seleccionar_facturas' ? `${it.facturas_cliente.length} factura(s) abiertas del cliente`
                  : '—';
                const vbDirectoOk = ['cobro_registrado_alegra', 'factura_exacta'].includes(it.propuesta);
                const conAdv = (it.advertencias || []).length > 0;
                return (
                  <tr key={it.movimiento.id} className={`border-b last:border-0 hover:bg-slate-50/50 ${conAdv ? 'bg-amber-50/60' : ''}`} data-testid={`pago-row-${it.movimiento.id}`}>
                    <td className="whitespace-nowrap px-3 py-2 text-xs">{fmtFecha(it.movimiento.fecha)}</td>
                    <td className="max-w-[220px] px-3 py-2" title={it.movimiento.detalle}>
                      <p className="truncate">{it.cliente_detectado || it.movimiento.detalle}</p>
                      {it.cliente?.via === 'rut' && (
                        <p className="flex items-center gap-1 text-[10px] text-sky-700" data-testid={`cliente-rut-${it.movimiento.id}`}>
                          <Fingerprint className="h-3 w-3" /> RUT {it.cliente.identificacion} identificado
                        </p>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-xs text-emerald-600">${CLP(it.movimiento.abono)}</td>
                    <td className="px-3 py-2">
                      <Badge className={`${pb.cls} hover:${pb.cls}`}>{pb.label}</Badge>
                      {conAdv && <Badge className="ml-1 bg-amber-200 text-amber-900 hover:bg-amber-200" data-testid={`badge-advertencia-${it.movimiento.id}`}><AlertTriangle className="mr-1 h-3 w-3" />Revisar</Badge>}
                    </td>
                    <td className="max-w-[340px] px-3 py-2 text-xs text-muted-foreground">
                      <p className="truncate" title={detalle}>{detalle}</p>
                      {(it.advertencias || []).map((a, i) => <p key={i} className="mt-0.5 whitespace-normal text-amber-800" data-testid={`advertencia-${it.movimiento.id}-${i}`}>⚠ {a}</p>)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right">
                      {vbDirectoOk && (
                        <Button size="sm" className={conAdv ? 'bg-amber-600 hover:bg-amber-700' : 'bg-emerald-600 hover:bg-emerald-700'} disabled={vbLoading === it.movimiento.id}
                          onClick={() => vbDirecto(it)} data-testid={`btn-vb-${it.movimiento.id}`}>
                          {vbLoading === it.movimiento.id ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : conAdv ? <AlertTriangle className="mr-1 h-3.5 w-3.5" /> : <ShieldCheck className="mr-1 h-3.5 w-3.5" />}
                          {conAdv ? 'Dar VB (revisar)' : 'Dar VB'}
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => openCorregir(it)} data-testid={`btn-corregir-${it.movimiento.id}`}>
                        <Pencil className="mr-1 h-3.5 w-3.5" /> {vbDirectoOk ? 'Corregir' : 'Elegir'}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={dialog.open} onOpenChange={(o) => !o && setDialog({ open: false, item: null })}>
        <DialogContent className="max-w-xl" data-testid="dialog-vb-corregir">
          <DialogHeader><DialogTitle>Elegir facturas para el VB</DialogTitle></DialogHeader>
          {dialog.item && (
            <div className="space-y-3">
              <div className="rounded-lg bg-slate-50 p-3 text-sm">
                <p className="font-medium">{dialog.item.movimiento.detalle}</p>
                <p className="text-xs text-muted-foreground">{fmtFecha(dialog.item.movimiento.fecha)} · Abono ${CLP(dialog.item.movimiento.abono)}{dialog.item.cliente ? ` · ${dialog.item.cliente.nombre} (RUT ${dialog.item.cliente.identificacion})` : ''}</p>
              </div>
              {(dialog.item.advertencias || []).length > 0 && (
                <div className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900" data-testid="dialog-advertencias">
                  {dialog.item.advertencias.map((a, i) => <p key={i}>⚠ {a}</p>)}
                </div>
              )}
              {(dialog.item.cobros_cliente || []).length > 0 && (
                <div data-testid="dialog-cobros-cliente">
                  <p className="mb-1 text-xs font-medium">Cobros ya registrados en Alegra para este cliente y monto</p>
                  <div className="space-y-1">
                    {dialog.item.cobros_cliente.map((c) => (
                      <label key={c.ref_id} className={`flex cursor-pointer items-center gap-2 rounded-lg border p-2 text-xs ${c.ya_conciliado ? 'opacity-50' : ''} ${cobroSel === c.ref_id ? 'border-sky-400 bg-sky-50' : ''}`}>
                        <input type="radio" name="cobro-cli" disabled={!!c.ya_conciliado} checked={cobroSel === c.ref_id} onChange={() => { setCobroSel(c.ref_id); setSel([]); }} data-testid={`vb-cobro-radio-${c.ref_id}`} />
                        <span className="flex-1 truncate">{c.ref_desc}</span>
                        {c.ya_conciliado && <span className="text-[10px] text-muted-foreground">ya conciliado ({typeof c.ya_conciliado === 'string' ? c.ya_conciliado : 'en esta bandeja'})</span>}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <p className="text-xs font-medium">Facturas abiertas del cliente</p>
              {(dialog.item.facturas_cliente.length > 0 ? dialog.item.facturas_cliente : dialog.item.facturas_exactas).length === 0 ? (
                <p className="rounded border border-dashed p-2 text-xs text-muted-foreground">
                  Sin facturas abiertas del cliente. Usa la pestaña Movimientos para conciliarlo como otro tipo.
                </p>
              ) : (
                <div className="max-h-52 space-y-1 overflow-y-auto">
                  {(dialog.item.facturas_cliente.length > 0 ? dialog.item.facturas_cliente : dialog.item.facturas_exactas).map((f) => (
                    <label key={f.alegra_id}
                      className={`flex cursor-pointer items-center gap-2 rounded-lg border p-2 text-xs ${sel.some((x) => x.alegra_id === f.alegra_id) ? 'border-emerald-400 bg-emerald-50' : ''}`}>
                      <input type="checkbox" checked={sel.some((x) => x.alegra_id === f.alegra_id)} onChange={() => { toggleSel(f); setCobroSel(null); }}
                        data-testid={`vb-factura-check-${f.numero}`} />
                      <span className="flex-1 truncate"><b>#{f.numero}</b> {f.cliente_nombre} · vence {fmtFecha(f.fecha_vencimiento)}
                        {f.fc_cxc ? ' · CxC ✓' : ''}</span>
                      <span className="font-mono">saldo ${CLP(f.saldo)}</span>
                    </label>
                  ))}
                </div>
              )}
              {sel.length > 0 && (
                <p className={`rounded px-2 py-1 text-xs font-medium ${Math.abs(sumaSel - dialog.item.movimiento.abono) <= 1 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}
                  data-testid="vb-suma-sel">
                  {sel.length} factura(s) · saldos ${CLP(sumaSel)} vs abono ${CLP(dialog.item.movimiento.abono)}
                  {Math.abs(sumaSel - dialog.item.movimiento.abono) <= 1 ? ' — calza ✓' : sel.length === 1 ? ' — se registrará pago PARCIAL en Alegra' : ' — con varias facturas debe calzar exacto'}
                </p>
              )}
              <Textarea value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Nota (opcional)" rows={2} data-testid="vb-nota" />
              <div className="rounded-lg border border-dashed border-amber-300 p-2 text-xs" data-testid="seccion-duplicado">
                <p className="mb-1 font-medium text-amber-900">¿El cliente pagó de más o duplicó un pago?</p>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" className="border-amber-400 text-amber-800" onClick={() => { setDupDlg({ open: true, item: dialog.item }); setDialog({ open: false, item: null }); }} data-testid="btn-duplicado-alegra">
                    <AlertTriangle className="mr-1 h-3.5 w-3.5" /> Pago duplicado → registrar ingreso en Alegra
                  </Button>
                  <Button size="sm" variant="ghost" className="text-amber-800" onClick={() => marcarDuplicadoSinAlegra(dialog.item)} data-testid="btn-duplicado-solo-marcar">
                    Solo marcar destacado (lo registro en Alegra después)
                  </Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false, item: null })}>Cancelar</Button>
            {cobroSel ? (
              <Button className="bg-sky-600 hover:bg-sky-700" disabled={vbLoading === dialog.item?.movimiento?.id}
                onClick={() => darVB(dialog.item, { usar_cobro_alegra: cobroSel, nota })} data-testid="btn-vb-confirmar-cobro">
                <CheckCircle2 className="mr-1 h-4 w-4" /> Dar VB con cobro #{cobroSel}
              </Button>
            ) : (
              <Button className="bg-emerald-600 hover:bg-emerald-700"
                disabled={sel.length === 0 || vbLoading === dialog.item?.movimiento?.id}
                onClick={() => darVB(dialog.item, { alegra_invoice_ids: sel.map((f) => String(f.alegra_id)), nota })}
                data-testid="btn-vb-confirmar">
                <CheckCircle2 className="mr-1 h-4 w-4" /> Dar VB con seleccionadas
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <IngresoExpressDialog open={dupDlg.open} mov={dupDlg.item?.movimiento} tipo="pago_duplicado_cliente"
        clienteInicial={dupDlg.item?.cliente ? { alegra_id: dupDlg.item.cliente.alegra_id, nombre: dupDlg.item.cliente.nombre, identificacion: dupDlg.item.cliente.identificacion } : null}
        onClose={() => setDupDlg({ open: false, item: null })} onDone={() => { load(); onChanged?.(); }} />
    </div>
  );
};
